export type TActivityReserveStatus = 'unused' | 'used' | 'expired' | 'refunded' | 'cancelled' | string

export const bookingStatusMap: Record<string, { textKey: string; className: string }> = {
  unused: { textKey: 'mkt.activityReserve.status.unused', className: 'active' },
  UNUSED: { textKey: 'mkt.activityReserve.status.unused', className: 'active' },
  pending_verify: { textKey: 'mkt.activityReserve.status.unused', className: 'active' },
  used: { textKey: 'mkt.activityReserve.status.used', className: 'muted' },
  USED: { textKey: 'mkt.activityReserve.status.used', className: 'muted' },
  verified: { textKey: 'mkt.activityReserve.status.used', className: 'muted' },
  expired: { textKey: 'mkt.activityReserve.status.expired', className: 'muted' },
  EXPIRED: { textKey: 'mkt.activityReserve.status.expired', className: 'muted' },
  refunded: { textKey: 'mkt.activityReserve.status.refunded', className: 'muted' },
  REFUNDED: { textKey: 'mkt.activityReserve.status.refunded', className: 'muted' },
  cancelled: { textKey: 'mkt.activityReserve.status.cancelled', className: 'muted' },
}

export const getActivityId = (item: any) => item?.activityId || item?.id || item?.activityReserveId || item?.activity?.id

export const getBookingId = (item: any) => item?.ordersId || item?.bookingId || item?.reservationId || item?.activityReserveOrderId || item?.id

export const getActivityName = (item: any) => item?.activityName || item?.name || item?.title || item?.activity?.name || '--'

export const getCover = (item: any) => item?.coverUrl || item?.coverImg || item?.cover || item?.imageUrl || item?.imgUrl || item?.activity?.coverUrl || item?.content?.coverUrl || ''

export const getFeeMode = (item: any) => {
  if (item?.feeType) return String(item.feeType).toLowerCase()
  if (item?.feeMode) return String(item.feeMode).toLowerCase()
  if (item?.chargeType) return String(item.chargeType).toLowerCase()
  return 'paid'
}

export const isFreeActivity = (item: any) => getFeeMode(item) === 'free'

export const formatPrice = (value: any) => {
  const num = Number(value || 0)
  if (!num) return i18n.t('mkt.activityReserve.free')
  return `¥${num.toFixed(num % 1 === 0 ? 0 : 2)}`
}

export const getMinPrice = (item: any) => {
  const value = item?.minPrice ?? item?.price
  if (value !== undefined && value !== null && value !== '') return value
  const centValue = item?.minPriceCent ?? item?.priceCent
  return centValue !== undefined && centValue !== null && centValue !== '' ? Number(centValue) / 100 : 0
}

export const getTicketPrice = (item: any) => {
  const value = item?.price ?? item?.ticketPrice
  if (value !== undefined && value !== null && value !== '') return Number(value)
  const centValue = item?.priceCent ?? item?.ticket?.priceCent
  return centValue !== undefined && centValue !== null && centValue !== '' ? Number(centValue) / 100 : 0
}

export const getStartAt = (item: any) => item?.startAt || item?.activityStartAt || item?.startTime || item?.beginAt || item?.activity?.startAt || ''

export const getEndAt = (item: any) => item?.endAt || item?.activityEndAt || item?.endTime || item?.finishAt || item?.activity?.endAt || ''

export const formatDateTime = (value: any) => {
  if (!value) return '--'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  const pad = (num: number) => String(num).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export const formatDateRange = (start: any, end: any) => {
  const startText = formatDateTime(start)
  const endText = formatDateTime(end)
  if (startText === '--' && endText === '--') return '--'
  if (startText === '--') return endText
  if (endText === '--') return startText
  if (startText === endText) return startText
  if (startText.slice(0, 10) === endText.slice(0, 10)) {
    return `${startText} - ${endText.slice(11)}`
  }
  return `${startText} - ${endText}`
}

export const formatDate = (value: any) => {
  const dateTime = formatDateTime(value)
  return dateTime === '--' ? dateTime : dateTime.slice(0, 10)
}

export const formatDateOnlyRange = (start: any, end: any) => {
  const startText = formatDate(start)
  const endText = formatDate(end)
  if (startText === '--' && endText === '--') return '--'
  if (startText === '--') return endText
  if (endText === '--') return startText
  return startText === endText ? startText : `${startText} - ${endText}`
}

export const getRemain = (item: any) => {
  const value = item?.remainCnt ?? item?.remainCount ?? item?.remainStock ?? item?.leftCnt ?? item?.remainingQuota ?? item?.stockAvailable ?? item?.availableStock ?? item?.capacityRemain
  if (value === undefined || value === null || value === '') return -1
  return typeof value === 'number' ? value : Number(value || 0)
}

export const getSessionCount = (item: any) => item?.sessionCnt || item?.sessionCount || item?.scheduleCount || item?.sessions?.length || item?.schedules?.length || 0

export const getSessionName = (item: any) => item?.sessionName || item?.name || item?.title || i18n.t('mkt.activityReserve.defaultSession')

export const getStoreName = (item: any) => item?.storeName || item?.name || item?.title || item?.store?.storeName || item?.store?.name || (item?.storeId || item?.id ? i18n.t('mkt.activityReserve.storeWithId', { id: item.storeId || item.id }) : '--')

export const getTicketName = (item: any) => item?.ticketName || item?.name || item?.title || i18n.t('mkt.activityReserve.defaultTicket')

export const getTicketQuantity = (item: any) => item?.quantity || item?.ticketQuantity || item?.ticket?.quantity || 1

const resolveContentValue = (value: any): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  if (Array.isArray(value)) return value.map(resolveContentValue).filter(Boolean).join('')
  return resolveContentValue(
    value.detailHtml ||
    value.detail ||
    value.richText ||
    value.html ||
    value.content ||
    value.description ||
    value.summary ||
    value.text ||
    value.value
  )
}

export const getActivitySummary = (item: any) => resolveContentValue(item?.summary || item?.description || item?.brief || item?.content?.summary || item?.content?.description)

export const getActivityDetailHtml = (item: any) => resolveContentValue(item?.detailHtml || item?.detail || item?.richText || item?.content?.detailHtml || item?.content?.detail || item?.content?.content || item?.content?.richText)

const normalizeStore = (store: any, storeMap: Record<string, any> = {}) => {
  const storeId = store?.storeId || store?.id
  const matchedStore = storeMap[String(storeId)] || {}
  return {
    ...matchedStore,
    ...store,
    storeId,
    storeName: store?.storeName || store?.name || store?.title || matchedStore?.storeName || matchedStore?.name || matchedStore?.title,
  }
}

const normalizeTicket = (ticket: any, ticketMap: Record<string, any> = {}) => {
  const activityTicketId = ticket?.activityTicketId || ticket?.ticketId || ticket?.id
  const matchedTicket = ticketMap[String(activityTicketId)] || {}
  const activityScheduleTicketId = ticket?.activityScheduleTicketId || ticket?.scheduleTicketId || (ticket?.activityTicketId ? ticket?.id : undefined)
  const mergedTicket = {
    ...matchedTicket,
    ...ticket,
  }
  return {
    ...mergedTicket,
    ticketId: activityScheduleTicketId || ticket?.ticketId || ticket?.id,
    activityTicketId,
    activityScheduleTicketId,
    ticketName: ticket?.ticketName || ticket?.name || ticket?.title || matchedTicket?.ticketName || matchedTicket?.name || matchedTicket?.title,
    price: getTicketPrice(mergedTicket),
    remainCnt: ticket?.remainCnt ?? ticket?.remainCount ?? ticket?.stockAvailable ?? ticket?.availableStock ?? ticket?.capacityRemain ?? matchedTicket?.remainCnt,
    soldOut: ticket?.soldOut ?? matchedTicket?.soldOut,
  }
}

export const normalizeActivityDetail = (res: any, storeList: any[] = []) => {
  const data = res?.data || res?.result || res || {}
  const activity = data?.activity || data
  const content = data?.content || activity?.content || {}
  const storeMap = storeList.reduce((map: Record<string, any>, store: any) => {
    const storeId = store?.storeId || store?.id
    if (storeId) map[String(storeId)] = store
    return map
  }, {})
  const schedules = data?.schedules || data?.sessions || data?.scheduleList || activity?.schedules || activity?.sessions || []
  const scheduleStores = schedules.reduce((list: any[], session: any) => [...list, ...(session?.stores || session?.storeList || [])], [])
  const stores = Object.keys(scheduleStores.reduce((map: Record<string, any>, store: any) => {
    const storeId = store?.storeId || store?.id
    if (storeId && !map[String(storeId)]) map[String(storeId)] = normalizeStore(store, storeMap)
    return map
  }, {})).map((storeId: string) => normalizeStore({ storeId }, storeMap))
  const scheduleTickets = scheduleStores.reduce((list: any[], store: any) => [...list, ...(store?.tickets || store?.ticketList || [])], [])
  const baseTickets = data?.tickets || data?.ticketList || activity?.tickets || []
  const baseTicketMap = baseTickets.map((ticket: any) => normalizeTicket(ticket)).reduce((map: Record<string, any>, ticket: any) => {
    const ids = [ticket?.activityTicketId, ticket?.ticketId, ticket?.id].filter(Boolean)
    ids.forEach((id: any) => { map[String(id)] = ticket })
    return map
  }, {})
  const tickets = scheduleTickets.map((ticket: any) => normalizeTicket(ticket, baseTicketMap))
  const ticketMap = tickets.reduce((map: Record<string, any>, ticket: any) => {
    const ids = [ticket?.activityTicketId, ticket?.ticketId, ticket?.id, ticket?.activityScheduleTicketId].filter(Boolean)
    ids.forEach((id: any) => { map[String(id)] = ticket })
    return map
  }, { ...baseTicketMap })

  return {
    ...data,
    ...activity,
    activityId: activity?.activityId || activity?.id,
    activityName: activity?.activityName || activity?.name || activity?.title,
    coverUrl: activity?.coverUrl || content?.coverUrl,
    summary: resolveContentValue(activity?.summary || content?.summary || content?.intro || content?.description),
    detail: resolveContentValue(activity?.detail || content?.detailHtml || content?.detail || content?.content || content?.richText),
    feeType: activity?.feeType || data?.feeType,
    stores,
    tickets,
    sessions: schedules.map((session: any) => {
      const sessionTickets = session?.tickets || session?.ticketList || session?.scheduleTickets || session?.activityScheduleTickets || []
      const sessionStores = session?.stores || session?.storeList || []
      const sessionStoreIds = session?.storeIds || sessionStores.map((store: any) => store?.storeId || store?.id)
      const sessionStoreId = session?.storeId || session?.activityStoreId
      const storeTicketsMap: Record<string, any[]> = sessionStores.reduce((map: Record<string, any[]>, store: any) => {
        const storeId = store?.storeId || store?.id
        const storeTickets = store?.tickets || store?.ticketList || []
        if (storeId) map[String(storeId)] = storeTickets.map((ticket: any) => normalizeTicket(ticket, ticketMap))
        return map
      }, {})
      return {
        ...session,
        sessionId: session?.sessionId || session?.activityScheduleId || session?.scheduleId || session?.id,
        sessionName: session?.sessionName || session?.name || session?.title,
        startAt: session?.startAt || session?.scheduleStartAt || session?.beginAt || session?.startTime,
        endAt: session?.endAt || session?.scheduleEndAt || session?.finishAt || session?.endTime,
        remainCnt: session?.remainCnt ?? session?.remainCount ?? session?.stockAvailable ?? session?.capacityRemain,
        storeIds: sessionStoreIds?.length ? sessionStoreIds : (sessionStoreId ? [sessionStoreId] : []),
        storeTicketsMap,
        tickets: sessionTickets.map((ticket: any) => normalizeTicket(ticket, ticketMap)),
      }
    }),
  }
}

export const maskMobile = (mobile: string) => {
  if (!mobile) return '--'
  return mobile.replace(/^(\d{3})\d+(\d{4})$/, '$1****$2')
}

export const normalizeList = (res: any) => {
  if (Array.isArray(res)) return res
  if (Array.isArray(res?.list)) return res.list
  if (Array.isArray(res?.data?.list)) return res.data.list
  if (Array.isArray(res?.data)) return res.data
  if (Array.isArray(res?.result?.list)) return res.result.list
  if (Array.isArray(res?.items)) return res.items
  if (Array.isArray(res?.records)) return res.records
  return []
}
